import React, { useCallback, useState } from "react"
import QRCode from "react-qr-code";

import { Ipad, IpadSetupToken } from "../../core/_models"
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAuth } from "../../AuthContext";
import { createIpadSetupToken } from "../../core/_requests";
import { useAxios, APIError } from "../../core/_axios";
import useLocalizedCopy from "../../hooks/useLocalizedCopy";

export function SetupIpadDeviceForm({ iPad }: { iPad: Ipad }) {
    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const [loading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [currentIpadSetupToken, setCurrentIpadSetupToken] = useState<IpadSetupToken | null>(null);
    const [showManualInstructions, setShowManualInstructions] = useState(false);

    const makeQrCodeLink = useCallback(async () => {
        if (loading) {
            return;
        }
        setCurrentIpadSetupToken(null);
        setLoadingError(null);
        setLoading(true);
        try {
            const resultingIpadSetupToken = await createIpadSetupToken(axios, iPad.unique_identifier);
            setCurrentIpadSetupToken(resultingIpadSetupToken);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : copy.Common.messages.errorOccurred
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, iPad.unique_identifier, loading])

    if (!iPad) {
        return null
    }

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={makeQrCodeLink}>
                        {copy.Common.messages.tryAgain}
                    </button>
                </div>
            </div>
        );
    }

    return <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
        <div className="col-span-2">
            <div className="flex flex-col gap-5 lg:gap-7.5">
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">
                            {copy.IPads.setup.title}
                        </h3>
                    </div>
                    <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                        <div className="flex flex-col gap-4">
                            {currentIpadSetupToken && <div>
                                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {copy.IPads.setup.step1}:
                                </label>
                                <div>
                                    <div style={{ background: 'white', padding: '16px' }}>
                                        <QRCode
                                            value={currentIpadSetupToken.qr_code_url}
                                            size={256}
                                        />
                                    </div>
                                </div>
                                <div className="text-sm text-gray-600 dark:text-white">
                                    ({copy.IPads.setup.troubleshooting})
                                </div>
                                <div className="mt-4">
                                    <label className="inline-flex items-center">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox"
                                            checked={showManualInstructions}
                                            onChange={e => setShowManualInstructions(e.target.checked)}
                                        />
                                        <span className="ml-2 text-sm text-gray-900 dark:text-white">
                                            {copy.IPads.setup.step3}
                                        </span>
                                    </label>
                                </div>
                                {showManualInstructions && (
                                    <div className="mt-2">
                                        <div>
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-900 dark:text-white">
                                                    {copy.IPads.setup.step3}:
                                                </label>
                                                <div className="mt-4 bg-gray-100 text-gray-900 text-2xl font-mono p-4 rounded-lg text-center">
                                                    {false ? <>
                                                        <pre className="font-mono text-lg">
                                                            O0I1lL9gqQwWvVuU8B6b5S2ZzXxCcPpDdEeFfAaMmNnHhJjKkRrTtYy
                                                        </pre>
                                                    </> : <>
                                                        {currentIpadSetupToken.short_unique_identifier || 'Invalid Token'}
                                                    </>}
                                                </div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                                    {copy.IPads.setup.deviceCode}: {currentIpadSetupToken.short_unique_identifier}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>}
                            <div>
                                <button className="btn btn-lg btn-light" onClick={
                                    makeQrCodeLink
                                }>
                                    {copy.IPads.setup.completeSetup}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}